import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { compose } from 'redux';
import { withRouter } from '@americanexpress/one-app-router';
import { connect, useSelector } from 'react-redux';
import { configureIguazuSSR } from 'iguazu-holocron';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import {
  OneExperimentsProvider,
  prefetchExperiments,
} from '@americanexpress/one-experiments';
import {
  loadLanguagePack,
  updateLocale,
  addErrorToReport,
} from '@americanexpress/one-app-ducks';
import DataManager from '@americanexpress/data-manager';
import {
  AmexDDLRegister,
  getAmexDataManagerConfig,
} from '@americanexpress/data-manager-utils';
import { OneFetchyeProvider } from 'fetchye-one-app';
import { IntlProvider } from 'react-intl';
import SharedScript, {
  Ensighten,
  Adobe,
  Maxymiser,
  ClickToChat,
} from '@americanexpress/shared-scripts';
import { withCookies, Cookies } from 'react-cookie';
import { loadProducts } from 'axp-product-ducks';

import moduleData from '../../package.json';
import { reducer } from '../ducks/duck';
import ErrorReporter from './ErrorHandling/ErrorReporter';
import OfflineWarning from './OfflineWarning/OfflineWarning';
import DigitalDataLayer from './DigitalDataLayer/DigitalDataLayer';
import './ManageBookingsRoot.scss';
import childRoutes from '../childRoutes';
import { convertLocaleToUpperCase } from '../helpers/helpers';
import { getClickToChat, getOneLoginDomain } from '../selectors/selectors';
import { readUserSession } from '../facade/readUserSession';
import { setAuthentication } from '../ducks/authDuck';
import Dynatrace from './Dynatrace/Dynatrace';
import { MODULENAME, ROOT_HOME_PAGE } from '../constants/constants';

export const ManageBookingsRoot = ({
  children,
  locale,
  languageData,
  params,
  setLocale,
  moduleState,
  cookies,
  router,
}) => {
  const getSafeLocaleData = (activeLocale) => {
    const [language, country] = activeLocale.split('-');
    return { activeLocale, language, country };
  };

  const hasClickToChat = useSelector(getClickToChat);
  const pathNameOfSummary = router?.location?.pathname;
  const isSummary = pathNameOfSummary?.includes('summary');

  const { country } = getSafeLocaleData(locale);
  React.useMemo(() => (global.BROWSER ? [window.AmexSession] : []), []);
  const oneLoginDomain = useSelector(getOneLoginDomain);

  React.useEffect(() => {
    if (global.BROWSER && window.AmexSession) {
      const targetURL = `${oneLoginDomain}/${locale.toLowerCase()}/account/login?DestPage=${window.location.href}`;
      window.AmexSession.options({
        target: encodeURIComponent(targetURL),
      });
    }
  }, [locale, oneLoginDomain]);
  const { ddlProviderProps, reactlyticsProps } = getAmexDataManagerConfig();
  if (
    params.locale
    && params.locale.includes('-')
    && params.locale !== locale.toLowerCase()
  ) {
    setLocale(convertLocaleToUpperCase(params.locale));
  }

  const products = moduleState.products.details;

  return (
    <ErrorReporter>
      <IntlProvider locale={locale} messages={languageData}>
        <div id={moduleData.name} data-module-name={moduleData.name}>
          <DataManager
            ddlProviderProps={ddlProviderProps}
            reactlyticsProps={reactlyticsProps}
          >
            <AmexDDLRegister />
            <Helmet
              {...{
                htmlAttributes: {
                  lang: locale,
                },
                title: languageData && languageData.title,
                link: [
                  {
                    rel: 'stylesheet',
                    href: 'https://www.aexp-static.com/cdaas/dls/packages/@americanexpress/dls/7.14.0/stylesheets/dls-max.min.css',
                  },
                  {
                    rel: 'icon',
                    href: 'https://www.americanexpress.com/favicon.ico',
                  },
                  {
                    rel: 'apple-touch-icon',
                    sizes: '152x152',
                    href: 'https://www.americanexpress.com/apple-touch-icon-152x152.png',
                  },
                ],
              }}
              bodyAttributes={{ class: 'dls-gray-01-bg' }}
              meta={[
                { 'http-equiv': 'X-UA-Compatible', content: 'IE=edge' },
                { charset: 'utf-8' },
                {
                  name: 'viewport',
                  content: 'width=device-width, initial-scale=1',
                },
                { name: 'application-name', content: 'American Express' },
                ...languageData
                && languageData?.isAppleSmartAppBannerEnabled
                && isSummary
                  ? [
                    {
                      name: 'apple-itunes-app',
                      content:
                          'app-id=6742465779, app-argument=amextravel://trips, affiliate-data=pt=13434&ct=MyTripsBanner&mt=8',
                    },
                  ]
                  : [],
              ]}
            >
              {languageData?.links && languageData?.isTealeafEnabled && (
                <script
                  src={languageData?.links?.tealeaf}
                  type="text/javascript"
                  async={true}
                />
              )}
            </Helmet>
            <SharedScript
              name="one-identity-session"
              version="^1.0.0"
              windowCheck="AmexSession"
            />
            <SharedScript
              name="one-stream-data-handler"
              version="^0.1.2"
              windowCheck="oneStreamData"
            />
            <OfflineWarning />
            <OneExperimentsProvider>
              <DigitalDataLayer products={products} cookiesData={cookies} />
              <OneFetchyeProvider>{children}</OneFetchyeProvider>
            </OneExperimentsProvider>
            <Adobe async={true} version="1.0.0" />
            <Maxymiser async={true} version="2.0.0" />
            {hasClickToChat ? (
              <ClickToChat
                version="^1.0.0"
                market={country}
                locale={locale}
                lineOfBusiness="Travel"
              />
            ) : (
              <Ensighten async={true} version="^0.1.0" />
            )}
            {languageData?.isDynatracefEnabled && <Dynatrace />}
          </DataManager>
        </div>
      </IntlProvider>
    </ErrorReporter>
  );
};

ManageBookingsRoot.childRoutes = childRoutes;

// appConfig should only be included in the node bundle
/* istanbul ignore next */
if (!global.BROWSER) {
  /* eslint-disable-next-line global-require --- disable global-require */
  ManageBookingsRoot.appConfig = require('../config/appConfig').default;
}

ManageBookingsRoot.propTypes = {
  children: PropTypes.node.isRequired,
  cookies: PropTypes.instanceOf(Cookies).isRequired,
  location: PropTypes.shape({}).isRequired,
  locale: PropTypes.string.isRequired,
  languageData: PropTypes.shape({
    title: PropTypes.string,
    isTealeafEnabled: PropTypes.bool,
    isDynatracefEnabled: PropTypes.bool,
    isAppleSmartAppBannerEnabled: PropTypes.bool,
    links: PropTypes.shape({
      tealeaf: PropTypes.string,
    }),
  }).isRequired,
  setLocale: PropTypes.func.isRequired,
  params: PropTypes.shape({
    locale: PropTypes.string,
  }).isRequired,
  router: PropTypes.node,
  moduleState: PropTypes.shape({
    products: PropTypes.shape({
      details: PropTypes.shape({}),
    }),
  }).isRequired,
};

export const loadModuleData = async ({ store, module, ownProps }) => {
  const { getState, dispatch } = store;
  global.getState = getState;
  const state = getState();
  const URL = global.BROWSER ? window.location.href : '';
  const authenticated = URL?.includes('-dev')
    ? true
    : await readUserSession(state);
  await Promise.allSettled([
    dispatch(loadLanguagePack(moduleData.name, { fallbackLocale: 'en-US' })),
    configureIguazuSSR({ store, module, ownProps }),
    dispatch(prefetchExperiments()),
    dispatch(loadProducts()),
    dispatch(setAuthentication(authenticated)),
  ]);
};

export const mapDispatchToProps = (dispatch) => ({
  setLocale: async (locale) => {
    try {
      await dispatch(updateLocale(locale));
      await dispatch(
        loadLanguagePack(moduleData.name, { fallbackLocale: 'en-US' })
      );
    } catch (error) {
      dispatch(
        addErrorToReport(error, {
          moduleName: MODULENAME,
          customerJourney: ROOT_HOME_PAGE,
        })
      );
    }
  },
});
ManageBookingsRoot.holocron = {
  name: moduleData.name,
  reducer,
  loadModuleData,
};

const hocChain = compose(
  oneAppModuleWrapper(moduleData.name),
  connect(null, mapDispatchToProps),
  withCookies,
  withRouter
);

export default hocChain(ManageBookingsRoot);
